if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.currentIndex = 0;
        this._observer = null;
      }

      connectedCallback() {
        this.track = this.querySelector('[id^="Slider-Gallery"]');
        this.slides = this.track ? [...this.track.querySelectorAll('.product__media-item')] : [];
        this.thumbItems = [...this.querySelectorAll('.product__thumbs-item')];
        this.counter = this.querySelector('.pg-counter');
        this.prevBtn = this.querySelector('.pg-btn-prev');
        this.nextBtn = this.querySelector('.pg-btn-next');
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.mediaLayout = this.dataset.mediaLayout;
        this.context = this.dataset.context;

        if (!this.track || !this.slides.length) return;

        this._setupButtons();
        this._setupThumbs();
        this._setupScrollTracking();
        this._setupZoom();
        this._updateCounter();
        this._updateThumbs();
        this._updateNav();
      }

      disconnectedCallback() {
        if (this._observer) this._observer.disconnect();
      }

      _setupZoom() {
        this.zoomOverlay = this.querySelector('.gallery-zoom');
        this.zoomImg     = this.querySelector('.gallery-zoom-img');
        if (!this.zoomOverlay || !this.zoomImg) return;

        // Move to <body> to escape any ancestor stacking context
        document.body.appendChild(this.zoomOverlay);

        this.addEventListener('click', (e) => {
          const btn = e.target.closest('.gallery-zoom-btn');
          if (!btn) return;
          this.zoomImg.src = btn.dataset.zoomSrc || '';
          this.zoomImg.alt = btn.dataset.zoomAlt || '';
          this.zoomOverlay.hidden = false;
          document.body.style.overflow = 'hidden';
        });

        this.zoomOverlay.querySelector('.gallery-zoom-close').addEventListener('click', () => this._closeZoom());

        this.zoomOverlay.addEventListener('click', (e) => {
          if (!e.target.closest('.gallery-zoom-wrap')) this._closeZoom();
        });

        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && !this.zoomOverlay.hidden) this._closeZoom();
        });
      }

      _closeZoom() {
        this.zoomOverlay.hidden = true;
        this.zoomImg.src = '';
        document.body.style.overflow = '';
      }

      _setupButtons() {
        if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.goTo(this.currentIndex - 1));
        if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.goTo(this.currentIndex + 1));
      }

      _setupThumbs() {
        this.thumbItems.forEach((thumb, i) => {
          thumb.addEventListener('click', () => this.goTo(i));
        });
      }

      _setupScrollTracking() {
        if (!('IntersectionObserver' in window)) return;
        this._observer = new IntersectionObserver(
          (entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                const idx = this.slides.indexOf(entry.target);
                if (idx !== -1 && idx !== this.currentIndex) {
                  this.currentIndex = idx;
                  this._updateCounter();
                  this._updateThumbs();
                }
              }
            });
          },
          { root: this.track, threshold: 0.6 }
        );
        this.slides.forEach(slide => this._observer.observe(slide));
      }

      goTo(index) {
        const total = this.slides.length;
        if (!total) return;
        index = ((index % total) + total) % total;
        const slide = this.slides[index];
        if (!slide) return;
        this.track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
        this.currentIndex = index;
        this._updateCounter();
        this._updateThumbs();
        this._updateNav();
        this._playActiveMedia(slide);
      }

      _updateCounter() {
        if (this.counter) {
          this.counter.textContent = `${this.currentIndex + 1} / ${this.slides.length}`;
        }
      }

      _updateThumbs() {
        this.thumbItems.forEach((thumb, i) => {
          thumb.classList.toggle('is-active', i === this.currentIndex);
        });

        const activeThumb = this.thumbItems[this.currentIndex];
        if (activeThumb) {
          const list = this.querySelector('[id^="Slider-Thumbnails"]');
          if (list) {
            const thumbLeft = activeThumb.offsetLeft;
            const thumbRight = thumbLeft + activeThumb.offsetWidth;
            const listScrollLeft = list.scrollLeft;
            const listWidth = list.offsetWidth;
            if (thumbLeft < listScrollLeft) {
              list.scrollTo({ left: thumbLeft, behavior: 'smooth' });
            } else if (thumbRight > listScrollLeft + listWidth) {
              list.scrollTo({ left: thumbRight - listWidth, behavior: 'smooth' });
            }
          }
        }
      }

      _updateNav() {
        this.classList.toggle('can-go-prev', this.currentIndex > 0);
      }

      _playActiveMedia(slide) {
        const model = slide.querySelector('product-model');
        if (model) model.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant || !Object.prototype.hasOwnProperty.call(variant, 'featured_media') || !variant.featured_media) return;

        const isMobile = window.matchMedia('(max-width: 767px)').matches;
        if (isMobile || this.enableDesktopSlider) {
          const pos = (variant.featured_media.position || 1) - 1;
          this.goTo(pos);
        } else {
          this._sortMediaItems(variant);
        }
      }

      _sortMediaItems(variant) {
        let items = [...this.querySelectorAll('.product__media-item')];

        items.sort((a, b) => a.dataset.mediaIndex - b.dataset.mediaIndex);

        items.some((item, i) => {
          if (item.dataset.mediaId == variant.featured_media.id) {
            const [el] = items.splice(i, 1);
            items.unshift(el);
            return true;
          }
        });

        this.track.innerHTML = '';
        items.forEach(item => this.track.appendChild(item));

        if (!window.matchMedia('(max-width: 767px)').matches && this.context !== 'quickview') {
          const selected = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (selected) window.scrollTo({ top: selected.offsetTop, behavior: 'smooth' });
        }
      }
    }
  );
}
